// helpers/tracking/consent.ts

/**
 * Einwilligungs-Modell (TDDDG § 25 / DSGVO Art. 6 Abs. 1 lit. a).
 *
 * - "functional" (notwendig) ist immer aktiv und braucht keine Einwilligung.
 * - "statistics": Google Analytics 4 + eigenes Formular-Statistik-Cookie ("analytics").
 * - "marketing": Meta Pixel / Conversions API + TikTok Pixel / Events API.
 *
 * Der aktuelle Stand liegt zusätzlich modulweit vor (getConsentState), damit
 * Helfer ohne Store-Import (z. B. metaCartSession) prüfen können, ob sie
 * überhaupt etwas im Endgerät speichern dürfen.
 */

export type ConsentCategory = 'statistics' | 'marketing';
export type ConsentChoices = Record<ConsentCategory, boolean>;

export type CookieSettings = {
    /** Speicherformat (3 = Kategorien getrennt; ≤ 2 = altes Format mit nur "analytics") */
    version: number;
    /** Stand der Kategorien/Anbieter, dem zugestimmt wurde. Wird CONSENT_REVISION erhöht, fragen wir neu. */
    revision: number;
    /** Zeitpunkt der Entscheidung (ISO). Bei Altbeständen unbekannt → null. */
    timestamp: string | null;
    functional: true;
    statistics: boolean;
    marketing: boolean;
    /** Altfeld für ältere Bundles/Leser: nur true, wenn allen optionalen Kategorien zugestimmt wurde. */
    analytics: boolean;
};

export const COOKIE_SETTINGS_KEY = 'cookieSettings';
export const STORAGE_VERSION = 3;

/**
 * Erhöhen, sobald sich Kategorien, Anbieter oder Zwecke ändern – dann wird
 * die Einwilligung bei allen Besucher:innen neu abgefragt.
 * 1 = Google Analytics 4, eigene Formular-Statistik, Meta Pixel/CAPI, TikTok Pixel/Events API.
 */
export const CONSENT_REVISION = 1;

const NO_CONSENT: ConsentChoices = {statistics: false, marketing: false};

let currentConsent: ConsentChoices = {...NO_CONSENT};

export function setConsentState(choices: ConsentChoices): void {
    currentConsent = {statistics: !!choices.statistics, marketing: !!choices.marketing};
}

export function getConsentState(): ConsentChoices {
    return {...currentConsent};
}

export function hasAnyConsent(): boolean {
    return currentConsent.statistics || currentConsent.marketing;
}

export function buildSettings(choices: ConsentChoices, timestamp: string | null, revision = CONSENT_REVISION): CookieSettings {
    return {
        version: STORAGE_VERSION,
        revision,
        timestamp,
        functional: true,
        statistics: !!choices.statistics,
        marketing: !!choices.marketing,
        analytics: !!choices.statistics && !!choices.marketing,
    };
}

export type ParsedSettings = {
    /** Gespeicherte Auswahl (zum Vorbelegen des Banners) */
    settings: CookieSettings;
    /** Tatsächlich wirksame Einwilligung (bei veralteter Revision: keine) */
    effective: ConsentChoices;
    /** Banner anzeigen? (keine oder veraltete Entscheidung) */
    needsPrompt: boolean;
};

/**
 * Liest den gespeicherten Stand – abwärtskompatibel:
 * Altformat {version ≤ 2, analytics} → "analytics: true" galt für alle damals
 * eingesetzten Dienste (GA4, Meta, TikTok) und damit für Statistik UND Marketing.
 */
export function parseStoredSettings(raw: string | null): ParsedSettings {
    const empty: ParsedSettings = {settings: buildSettings(NO_CONSENT, null, 0), effective: {...NO_CONSENT}, needsPrompt: true};
    if (!raw) return empty;

    let parsed: Partial<CookieSettings> | null = null;
    try {
        parsed = JSON.parse(raw) as Partial<CookieSettings>;
    } catch {
        return empty;
    }
    if (!parsed || typeof parsed !== 'object') return empty;

    const isNewFormat = typeof parsed.statistics === 'boolean' || typeof parsed.marketing === 'boolean';
    if (!isNewFormat) {
        const legacy = !!parsed.analytics;
        const choices = {statistics: legacy, marketing: legacy};
        return {
            settings: buildSettings(choices, null, CONSENT_REVISION),
            effective: choices,
            needsPrompt: false,
        };
    }

    const choices = {statistics: !!parsed.statistics, marketing: !!parsed.marketing};
    const revision = typeof parsed.revision === 'number' ? parsed.revision : 0;
    const timestamp = typeof parsed.timestamp === 'string' ? parsed.timestamp : null;
    const outdated = revision < CONSENT_REVISION;
    return {
        settings: buildSettings(choices, timestamp, revision),
        effective: outdated ? {...NO_CONSENT} : choices,
        needsPrompt: outdated,
    };
}

/* -------------------- Cookies im Browser entfernen -------------------- */

/** Alle Domain-Varianten, unter denen ein Cookie gesetzt sein kann (host, .host, Eltern-Domains). */
function domainVariants(): Array<string | undefined> {
    const host = typeof window !== 'undefined' ? window.location.hostname : '';
    const variants: Array<string | undefined> = [undefined];
    if (!host || /^[\d.]+$/.test(host) || host === 'localhost') return variants;
    const parts = host.split('.');
    for (let i = 0; i < parts.length - 1; i++) {
        const d = parts.slice(i).join('.');
        variants.push(d, `.${d}`);
    }
    return variants;
}

function expireCookie(name: string): void {
    for (const domain of domainVariants()) {
        const d = domain ? `; domain=${domain}` : '';
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0; path=/${d}`;
    }
}

/**
 * Löscht First-Party-Cookies (exakter Name oder Präfix). Third-Party-Cookies
 * der Anbieter-Domains und httpOnly-Cookies sind per JavaScript nicht erreichbar.
 */
export function removeCookies(names: string[], prefixes: string[] = []): void {
    if (typeof document === 'undefined') return;
    try {
        const present = document.cookie.split(';').map((c) => c.split('=')[0].trim()).filter(Boolean);
        const targets = new Set<string>(names);
        for (const p of prefixes) {
            present.filter((n) => n.startsWith(p)).forEach((n) => targets.add(n));
        }
        targets.forEach((n) => expireCookie(n));
    } catch {
        // noop
    }
}

/** Session-Daten der Anmeldestrecke (Event-IDs, Warenkorb-Snapshot) entfernen. */
export function removeSignupSessionData(): void {
    try {
        const keys: string[] = [];
        for (let i = 0; i < sessionStorage.length; i++) {
            const k = sessionStorage.key(i);
            if (k && k.startsWith('signup.')) keys.push(k);
        }
        keys.forEach((k) => sessionStorage.removeItem(k));
    } catch {
        // noop
    }
}
